import { useQuery } from "@tanstack/react-query"
import { PROVIDER_THREADS_CACHE_KEY, type ProviderThreadList } from "@internal/messages/provider-threads-cache"
import { useCurrentUser } from "#/components/context/user/UserContext"
import useCarrotPlan from "#/components/carrot-plans/hooks/useCarrotPlan"
import { useGetCarrotPlanRequestStatus } from "#/components/carrot-plans/hooks/useCarrotPlanRequestStatus"
import { useUserRole } from "#/components/partner-access/hooks/usePartnerAccess"
import { useCalibratedEngagementEnabled } from "#/hooks/calibratedEngagement/useCalibratedEngagement"
import { providerApi } from "#/pages/consumer/messages/messagesHostApi"
import { useConsumerRoutesEnabled } from "#/services/launchdarkly/useConsumerRoutesEnabled"
import { useMemberProgram } from "#/services/user/hooks/useMemberProgram"
import { allOf, anyOf, fromBoolean, type Gate } from "./gate"
import { type FeatureAvailability, useFeatureAvailability } from "./useFeatureAvailability"

export type { Gate } from "./gate"

type MenuItem =
  | "appointments"
  | "carrotRx"
  | "providerFinder"
  | "exclusiveDiscounts"
  | "enterpriseFaqs"
  | "consumerFaq"
  // Also the Messages Clinicians tab and the nav's provider unread dot.
  | "clinicians"
  | "financialSupport"

// Config only and the same for every program, except carrotRx and clinicians (see below).
type FeatureRoute =
  | "appointments"
  | "providerFinder"
  | "exclusiveDiscountsOffered"
  | "helpCenter"
  | "carrotRx"
  | "clinicians"

export type NavAvailability = {
  // Menu items, shown only on a definitive "on".
  menu: Record<MenuItem, boolean>
  // Secondary surfaces: link to a feature only when its route is on.
  show: Record<FeatureRoute, boolean>
  // Four states, for FeatureRouteGate and the few pages that wait on or redirect by a gate.
  route: Record<FeatureRoute, Gate>
}

function not(gate: Gate): Gate {
  if (gate === "on") return "off"
  if (gate === "off") return "on"
  return gate
}

function readGate<T>({ data, isError }: { data?: T; isError: boolean }, isOn: (data: T) => boolean): Gate {
  if (data) return fromBoolean(isOn(data))
  return isError ? "error" : "loading"
}

// A consumer-only rule: applies to consumers, is "on" for everyone else, and waits on an unknown program.
function forConsumers(consumer: Gate, gate: Gate): Gate {
  if (consumer === "on") return gate
  return consumer === "off" ? "on" : consumer
}

function shownWhenOn<K extends string>(gates: Record<K, Gate>): Record<K, boolean> {
  return Object.fromEntries(Object.entries<Gate>(gates).map(([key, gate]) => [key, gate === "on"])) as Record<
    K,
    boolean
  >
}

// The one place program rules are combined with member.features (which is config only), so components
// don't write `isConsumer && feature` themselves.
export function useNavAvailability(): NavAvailability {
  const features = useFeatureAvailability()
  const { company } = useCurrentUser()
  const program = useMemberProgram()
  const consumerRoutesEnabled = useConsumerRoutesEnabled()
  const { isPartnerAccessingAccount, isUserRoleLoading } = useUserRole()
  const carrotPlanQuery = useCarrotPlan()
  const cpRequestStatusQuery = useGetCarrotPlanRequestStatus()
  const calibratedEngagementEnabled = useCalibratedEngagementEnabled()

  const readFeature = (name: keyof FeatureAvailability): Gate =>
    features.status === "ready" ? fromBoolean(features.features[name]) : features.status

  const consumer = readGate(program, ({ programType }) => programType === "CONSUMER")
  const notPartner: Gate = isUserRoleLoading ? "loading" : fromBoolean(isPartnerAccessingAccount !== true)

  // A plain boolean as before: while its reads load, it hides nothing.
  const hideForCalibratedEngagement = Boolean(
    calibratedEngagementEnabled.data &&
      !carrotPlanQuery.hasCarrotPlan &&
      !cpRequestStatusQuery.data?.carrotPlanIsOnTheWay
  )

  // Members whose appointments were turned off keep Clinicians if they already have threads. The list
  // is only asked for in that case, and shares its cache entry with the unread dot.
  const askForProviderThreads = readFeature("appointments") === "off" && consumer === "on" && notPartner === "on"
  const providerThreadsQuery = useQuery<ProviderThreadList>(
    PROVIDER_THREADS_CACHE_KEY,
    () => providerApi.getThreads(),
    {
      enabled: askForProviderThreads,
      refetchOnWindowFocus: false,
      retry: 1
    }
  )
  const providerThreads = (): Gate => {
    if (readFeature("appointments") !== "off") return "off"
    // Not asked until the program and role are known: wait (or fail open) rather than read "off".
    if (!askForProviderThreads) return allOf(consumer, notPartner)
    return readGate(providerThreadsQuery, ({ threads }) => threads.length > 0)
  }
  const hasProviderThreads = providerThreads()

  const gatesFor = (feature: (name: keyof FeatureAvailability) => Gate) => {
    const appointments = feature("appointments")
    const clinicians = allOf(consumer, notPartner, anyOf(appointments, hasProviderThreads))

    const menu: Record<MenuItem, Gate> = {
      appointments: allOf(consumer, appointments),
      carrotRx: allOf(consumer, feature("carrotRx")),
      providerFinder: allOf(feature("providerFinder"), fromBoolean(!hideForCalibratedEngagement)),
      exclusiveDiscounts: feature("exclusiveDiscountsOffered"),
      // Enterprise gets the /faqs help center, consumers their own FAQ page; never both.
      enterpriseFaqs: allOf(not(consumer), feature("helpCenter")),
      consumerFaq: allOf(consumer, fromBoolean(consumerRoutesEnabled), feature("helpCenter")),
      clinicians,
      financialSupport: allOf(
        not(consumer),
        fromBoolean(!company.carrotLite),
        fromBoolean(!hideForCalibratedEngagement)
      )
    }
    const route: Record<FeatureRoute, Gate> = {
      appointments,
      providerFinder: feature("providerFinder"),
      exclusiveDiscountsOffered: feature("exclusiveDiscountsOffered"),
      helpCenter: feature("helpCenter"),
      // Until the program is known, hold or fail open: enterprise config may well have Carrot Rx off.
      carrotRx: forConsumers(consumer, feature("carrotRx")),
      // Enterprise members (sent on to /messages by the shell) and partners (shown its member-only
      // notice) keep the route; everyone else gets the menu's rule.
      clinicians: consumer === "off" || notPartner === "off" ? forConsumers(consumer, "on") : clinicians
    }
    return { menu, route }
  }
  const { route } = gatesFor(readFeature)
  // A failed member.features read fails open in menus and links too, as routes do; program and role
  // read errors don't, so enterprise members never get consumer-only items from a failed program read.
  const shown = gatesFor((name) => (features.status === "error" ? "on" : readFeature(name)))

  return { menu: shownWhenOn(shown.menu), show: shownWhenOn(shown.route), route }
}
